"use client";

import { useEffect, useRef, useState, useCallback } from "react";
import { formatDistanceToNow } from "date-fns";
import { ImageIcon, Box, Loader2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BeforeAfterSlider } from "@/components/ui/before-after-slider";
import { createStudioEnvironment, VIEWER_LIGHT_LEVELS, type StudioEnvironment } from "@/lib/studio-lighting";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

import type { GenerationType } from "@/types/generation";

interface GenerationModalProps {
  generation: any | null;
  type: GenerationType;
  open: boolean;
  onClose: () => void;
}

const VIEWER_HEIGHT = 460;

type BgColor = "light" | "neutral" | "dark";

const BG_COLORS: Record<BgColor, number> = { light: 0xf5f5f5, neutral: 0x888888, dark: 0x222222 };

const PRESETS = [
  { id: "studio", label: "Studio", intensity: 1.5, exposure: 1.5, bg: "light" as BgColor },
  { id: "bright", label: "Bright", intensity: 2.5, exposure: 2.0, bg: "light" as BgColor },
  { id: "soft",   label: "Soft",   intensity: 1.2, exposure: 1.3, bg: "neutral" as BgColor },
  { id: "dramatic", label: "Dramatic", intensity: 1.8, exposure: 1.2, bg: "dark" as BgColor },
];

const BG_OPTIONS: { id: BgColor; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "neutral", label: "Neutral" },
  { id: "dark", label: "Dark" },
];

function ThreeDViewer({ src }: { src: string }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState(false);
  const [modelReady, setModelReady] = useState(false);
  const [intensity, setIntensity] = useState(1.5);
  const [exposure, setExposure] = useState(1.5);
  const [bg, setBg] = useState<BgColor>("light");

  // Refs to live Three.js objects so controls can update them without re-mounting
  const threeRef = useRef<{
    renderer: any;
    scene: any;
    lights: { hemi: any; main: any; fill: any; back: any; front: any; bottom: any };
  } | null>(null);

  const applyIntensity = useCallback((value: number) => {
    const t = threeRef.current;
    if (!t) return;
    t.scene.environmentIntensity = VIEWER_LIGHT_LEVELS.environment * value;
    t.lights.hemi.intensity   = VIEWER_LIGHT_LEVELS.hemisphere * value;
    t.lights.main.intensity   = VIEWER_LIGHT_LEVELS.main * value;
    t.lights.fill.intensity   = VIEWER_LIGHT_LEVELS.fill * value;
    t.lights.back.intensity   = VIEWER_LIGHT_LEVELS.back * value;
    t.lights.front.intensity  = VIEWER_LIGHT_LEVELS.front * value;
    t.lights.bottom.intensity = VIEWER_LIGHT_LEVELS.bottom * value;
  }, []);

  const applyExposure = useCallback((value: number) => {
    if (threeRef.current) threeRef.current.renderer.toneMappingExposure = value;
  }, []);


  useEffect(() => {
    if (!src || !containerRef.current) return;
    const container = containerRef.current;
    let animationId: number;
    let renderer: any;
    let environment: StudioEnvironment | undefined;
    (async () => {
      const THREE = await import("three");
      const { GLTFLoader } = await import("three/examples/jsm/loaders/GLTFLoader.js");
      const { OrbitControls } = await import("three/examples/jsm/controls/OrbitControls.js");
      if (!containerRef.current) return;
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: "high-performance" });
      renderer.setPixelRatio(window.devicePixelRatio);
      renderer.setSize(container.clientWidth, container.clientHeight);
      renderer.toneMapping = THREE.ACESFilmicToneMapping;
      renderer.toneMappingExposure = 1.5;
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.domElement.style.display = "block";
      renderer.domElement.style.width = "100%";
      renderer.domElement.style.height = "100%";
      container.appendChild(renderer.domElement);

      const scene = new THREE.Scene();
      scene.background = new THREE.Color(BG_COLORS.light);

      const camera = new THREE.PerspectiveCamera(45, container.clientWidth / container.clientHeight, 0.1, 1000);
      camera.position.set(1, 2.55, 5);
      camera.lookAt(0, 0, 0);

      // Metal shows only what it reflects; without surroundings it renders black (see studio-lighting.ts).
      environment = createStudioEnvironment(THREE, renderer);
      scene.environment = environment.texture;
      scene.environmentIntensity = VIEWER_LIGHT_LEVELS.environment;

      const hemi   = new THREE.HemisphereLight(0xffffff, 0xcccccc, VIEWER_LIGHT_LEVELS.hemisphere);
      hemi.position.set(0, 20, 0);
      const main   = new THREE.DirectionalLight(0xffffff, VIEWER_LIGHT_LEVELS.main); main.position.set(5, 10, 7);
      const fill   = new THREE.DirectionalLight(0xffffff, VIEWER_LIGHT_LEVELS.fill); fill.position.set(-5, 5, -5);
      const back   = new THREE.DirectionalLight(0xffffff, VIEWER_LIGHT_LEVELS.back); back.position.set(0, 5, -10);
      const front  = new THREE.DirectionalLight(0xffffff, VIEWER_LIGHT_LEVELS.front); front.position.set(0, 3, 10);
      const bottom = new THREE.DirectionalLight(0xffffff, VIEWER_LIGHT_LEVELS.bottom); bottom.position.set(0, -5, 0);
      scene.add(hemi, main, fill, back, front, bottom);

      threeRef.current = { renderer, scene, lights: { hemi, main, fill, back, front, bottom } };

      const controls = new OrbitControls(camera, renderer.domElement);
      controls.enableDamping = true;
      controls.dampingFactor = 0.25;
      controls.autoRotate = true;
      controls.autoRotateSpeed = 1.0;

      const loader = new GLTFLoader();
      loader.load(
        src,
        (gltf: any) => {
          const model = gltf.scene;
          const box = new THREE.Box3().setFromObject(model);
          const center = box.getCenter(new THREE.Vector3());
          const size = box.getSize(new THREE.Vector3());
          model.position.sub(center);
          const maxDim = Math.max(size.x, size.y, size.z);
          if (maxDim > 0) model.scale.multiplyScalar(3 / maxDim);
          scene.add(model);
          controls.target.set(0, 0, 0);
          controls.update();
          setLoading(false);
          setModelReady(true);
        },
        (xhr: any) => { if (xhr.total > 0) setProgress(Math.round((xhr.loaded / xhr.total) * 100)); },
        () => { setError(true); setLoading(false); }
      );

      const animate = () => {
        animationId = requestAnimationFrame(animate);
        controls.update();
        renderer.render(scene, camera);
      };
      animate();

      const onResize = () => {
        if (!containerRef.current) return;
        camera.aspect = container.clientWidth / container.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(container.clientWidth, container.clientHeight);
      };
      window.addEventListener("resize", onResize);
    })();

    return () => {
      cancelAnimationFrame(animationId);
      threeRef.current = null;
      environment?.dispose();
      if (renderer) {
        renderer.dispose();
        if (container.contains(renderer.domElement)) container.removeChild(renderer.domElement);
      }
    };
  }, [src]);

  const handlePreset = (preset: typeof PRESETS[number]) => {
    setIntensity(preset.intensity);
    setExposure(preset.exposure);
    setBg(preset.bg);
    applyIntensity(preset.intensity);
    applyExposure(preset.exposure);
    // Need THREE.Color — grab from already-loaded module
    if (threeRef.current) {
      threeRef.current.scene.background = { r: 0, g: 0, b: 0 }; // placeholder reset
      import("three").then(({ Color }) => {
        if (threeRef.current) threeRef.current.scene.background = new Color(BG_COLORS[preset.bg]);
      });
    }
  };

  const handleBg = (color: BgColor) => {
    setBg(color);
    import("three").then(({ Color }) => {
      if (threeRef.current) threeRef.current.scene.background = new Color(BG_COLORS[color]);
    });
  };

  return (
    <div className="space-y-3">
      <div className="relative w-full rounded-xl overflow-hidden bg-neutral-100" style={{ height: VIEWER_HEIGHT }}>
        <div ref={containerRef} className="w-full h-full" />
        {loading && !error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-muted-foreground pointer-events-none">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">{progress > 0 ? `Loading model… ${progress}%` : "Loading 3D viewer…"}</p>
          </div>
        )}
        {error && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-destructive pointer-events-none">
            <Box className="h-8 w-8" />
            <p className="text-sm">Failed to load 3D model</p>
          </div>
        )}
      </div>

      {modelReady && (
        <div className="rounded-xl border bg-card p-4 space-y-4">
          <p className="text-xs font-semibold text-muted-foreground">💡 Lighting</p>

          {/* Presets */}
          <div className="flex gap-2">
            {PRESETS.map((p) => (
              <Button key={p.id} variant="outline" size="sm" className="flex-1 text-xs" onClick={() => handlePreset(p)}>
                {p.label}
              </Button>
            ))}
          </div>

          {/* Brightness */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">Brightness</span>
              <span className="text-xs text-muted-foreground">{Math.round(intensity * 100)}%</span>
            </div>
            <input
              type="range" min={0.5} max={4} step={0.1} value={intensity}
              onChange={(e) => { const v = parseFloat(e.target.value); setIntensity(v); applyIntensity(v); }}
              className="w-full accent-primary"
            />
          </div>

          {/* Exposure */}
          <div className="space-y-1">
            <div className="flex justify-between">
              <span className="text-xs text-muted-foreground">Exposure</span>
              <span className="text-xs text-muted-foreground">{Math.round(exposure * 100)}%</span>
            </div>
            <input
              type="range" min={0.5} max={3} step={0.1} value={exposure}
              onChange={(e) => { const v = parseFloat(e.target.value); setExposure(v); applyExposure(v); }}
              className="w-full accent-primary"
            />
          </div>

          {/* Background */}
          <div className="space-y-1">
            <span className="text-xs text-muted-foreground">Background</span>
            <div className="flex gap-2 mt-1">
              {BG_OPTIONS.map((o) => (
                <Button
                  key={o.id}
                  variant={bg === o.id ? "default" : "outline"}
                  size="sm"
                  className="flex-1 text-xs"
                  onClick={() => handleBg(o.id)}
                >
                  {o.label}
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function NoOutput() {
  return (
    <div className="w-full h-full min-h-[400px] rounded-xl bg-muted flex flex-col items-center justify-center gap-3 text-muted-foreground">
      <ImageIcon className="h-12 w-12 opacity-30" />
      <p className="text-sm">No output available</p>
    </div>
  );
}

function getStatusColor(status: string) {
  switch (status) {
    case "COMPLETED":
      return "bg-green-500 hover:bg-green-600";
    case "FAILED":
      return "destructive";
    case "PROCESSING":
      return "bg-blue-500 hover:bg-blue-600";
    default:
      return "secondary";
  }
}

export function GenerationModal({
  generation,
  type,
  open,
  onClose,
}: GenerationModalProps) {
  if (!generation) return null;

  const inputImages: string[] = generation.inputImageUrls ?? [];
  const inputImage = inputImages[0];
  const outputUrl = generation.outputUrls?.[0];
  const is3D = type === "2d-to-3d";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl w-full p-0 overflow-y-auto max-h-[90vh] gap-0">
        <DialogHeader className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {is3D ? (
                <Box className="h-5 w-5 text-muted-foreground" />
              ) : (
                <ImageIcon className="h-5 w-5 text-muted-foreground" />
              )}
              <DialogTitle className="text-base font-semibold">
                Generation Detail
              </DialogTitle>
            </div>
            <div className="flex items-center gap-3">
              <Badge
                className={getStatusColor(generation.status)}
                variant={generation.status === "FAILED" ? "destructive" : "default"}
              >
                {generation.status}
              </Badge>
              <span className="text-xs text-muted-foreground">
                {formatDistanceToNow(new Date(generation.createdAt), {
                  addSuffix: true,
                })}
              </span>
            </div>
          </div>
        </DialogHeader>

        <div className="px-6 pb-6">
          {is3D ? (
            <div className="w-full">
              {outputUrl ? (
                <ThreeDViewer src={outputUrl} />
              ) : (
                <NoOutput />
              )}
              {inputImages.length > 0 && (
                <div className="mt-4 flex items-center gap-3">
                  <p className="text-xs font-medium text-muted-foreground shrink-0">
                    Input {inputImages.length > 1 ? "Images" : "Image"}
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    {inputImages.map((url, i) => (
                      <div key={i} className="w-16 h-16 rounded-md overflow-hidden bg-muted shrink-0">
                        <img
                          src={url}
                          alt={`Input ${i + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              {inputImage && outputUrl ? (
                <div>
                  <div style={{ height: "460px" }}>
                    <BeforeAfterSlider
                      key={generation.id}
                      beforeImage={inputImage}
                      afterImage={outputUrl}
                      transparentAfter={type === "magic-background"}
                    />
                  </div>
                  {inputImages.length > 1 && (
                    <div className="mt-4 flex items-center gap-3">
                      <p className="text-xs font-medium text-muted-foreground shrink-0">
                        Input Images
                      </p>
                      <div className="flex gap-2 flex-wrap">
                        {inputImages.map((url, i) => (
                          <div key={i} className="w-16 h-16 rounded-md overflow-hidden bg-muted shrink-0">
                            <img
                              src={url}
                              alt={`Input ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">
                      Input {inputImages.length > 1 ? "Images" : "Image"}
                    </p>
                    {inputImages.length > 0 ? (
                      <div className="flex flex-col gap-2">
                        {inputImages.map((url, i) => (
                          <div key={i} className="aspect-square rounded-xl overflow-hidden bg-muted">
                            <img
                              src={url}
                              alt={`Input ${i + 1}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                        <NoOutput />
                      </div>
                    )}
                  </div>
                  <div className="space-y-2">
                    <p className="text-xs font-medium text-muted-foreground">Output</p>
                    <div className="aspect-square rounded-xl overflow-hidden bg-muted">
                      {outputUrl ? (
                        <img
                          src={outputUrl}
                          alt="Output"
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <NoOutput />
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {generation.errorMessage && (
            <div className="mt-4 bg-destructive/10 p-3 rounded-lg text-sm text-destructive">
              Error: {generation.errorMessage}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
